import axios from 'axios'
import { add, getDay, sub } from 'date-fns'
import { useRouter } from 'next/router'
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'
import { MyBLINK } from '@/src/@types/blinkadmin'
import { flchUrl, fullFlchUrl, get } from '@/src/BlinkAdminApiClient'
import trackEvent from '@/src/lib/tracking'

export type MergedSlot = {
  id: string
  start: string
  end: string
  userIds: string[]
  isFavored: boolean[]
}

export enum BookingState {
  Start,
  InstructorSelected,
  LocationSelected,
  GearboxSelected,
  MergedSlotSelected,
  SlotSelected,
  Summary,
}

export type BookingSelection = {
  config: {
    selectedDate: Date
    step: 'halfWeek' | 'week' | null
    dialogOpen: boolean
    currentDialogStep: 'location' | 'gearbox'
  }
} & (
  | Start
  | InstructorSelected
  | AddressSelected
  | GearboxSelected
  | MergedSlotSelected
  | SlotSelected
)

type Start = {
  instructor: null
  location: null
  address: null
  gearbox: null
  mergedSlot: null
  slot: null
  config: {
    state: BookingState.Start
  }
}

type InstructorSelected = {
  instructor: MyBLINK.BookableUser
  location: null
  address: null
  gearbox: null
  mergedSlot: null
  slot: null
  config: {
    state: BookingState.InstructorSelected
  }
}

type AddressSelected = {
  instructor: MyBLINK.BookableUser
  location: MyBLINK.BookableLocation
  address: MyBLINK.BookableAddress
  gearbox: null
  mergedSlot: null
  slot: null
  config: {
    state: BookingState.LocationSelected
  }
}

type GearboxSelected = {
  instructor: MyBLINK.BookableUser
  location: MyBLINK.BookableLocation
  address: MyBLINK.BookableAddress
  gearbox: 'manual' | 'automatic'
  mergedSlot: null
  slot: null
  config: {
    state: BookingState.GearboxSelected
  }
}

type MergedSlotSelected = {
  instructor: MyBLINK.BookableUser
  location: MyBLINK.BookableLocation
  address: MyBLINK.BookableAddress
  gearbox: 'manual' | 'automatic'
  mergedSlot: MergedSlot
  slot: null
  config: {
    state: BookingState.MergedSlotSelected
  }
}

type SlotSelected = {
  instructor: MyBLINK.BookableUser
  location: MyBLINK.BookableLocation
  address: MyBLINK.BookableAddress
  gearbox: 'manual' | 'automatic'
  mergedSlot: MergedSlot
  slot: MyBLINK.BookableSlot
  config: {
    state: BookingState.SlotSelected | BookingState.Summary
  }
}

export const BookingContext = createContext<
  | (BookingSelection & {
      confirmAddress(
        location: MyBLINK.BookableLocation,
        address: MyBLINK.BookableAddress
      ): void
      confirmGearbox(gearbox: 'manual' | 'automatic'): void
      confirmMergedSlot(mergedSlot: MergedSlot | null): void
      removeSlot(): void
      confirmSlot(slot: MyBLINK.BookableSlot | null): void
      goToSummary(): void
      goToBooking(): void
      changeSelectedDate(
        step: 'halfWeek' | 'week',
        direction: 'before' | 'next'
      ): void
      setSelectedDate(date: Date): void
      setStep(step: 'halfWeek' | 'week'): void
      setDialogStep(currentDialogStep: 'location' | 'gearbox'): void
      setDialogOpen(open: boolean): void
    })
  | undefined
>(undefined)

export default function BookingContextProvider({
  children,
}: {
  children: ReactNode
}) {
  const router = useRouter()

  const [state, setState] = useState<BookingSelection>({
    instructor: null,
    location: null,
    address: null,
    gearbox: null,
    mergedSlot: null,
    slot: null,
    config: {
      state: BookingState.Start,
      selectedDate: new Date(),
      step: null,
      dialogOpen: false,
      currentDialogStep: 'location',
    },
  })
  const [urlQueryLoaded, setUrlQueryLoaded] = useState(false)

  useEffect(() => {
    trackEvent({
      event: 'trial_booking_opened',
    })
  }, [])

  useEffect(() => {
    if (!router.isReady) return

    loadFromQuery()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])

  useEffect(() => {
    if (!urlQueryLoaded) return
    const query: { [key: string]: string | string[] } = {}
    query.instructorContext = router.query.instructorContext!
    if (state.instructor) {
      query.instructorIds = [state.instructor.id]
    }
    if (state.location) {
      query.location = state.location.slug
    }
    if (state.address) {
      query.addressId = state.address.id.toString()
    }
    if (state.gearbox) {
      query.gearbox = state.gearbox
    }

    router.replace(
      {
        pathname: router.asPath.split(/[?#]/)[0],
        query,
      },
      undefined,
      { shallow: true }
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, urlQueryLoaded])

  const loadFromQuery = async () => {
    const queryInstructorId = router.query.instructorContext
    const queryLocation = router.query.location
    const queryAddressId = router.query.addressId
    const queryGearbox = router.query.gearbox

    const instructorId = parseQuery(queryInstructorId, 'array') as string | null
    if (!instructorId) {
      confirmState(state)
      return
    }

    const instructor = await get<MyBLINK.BookableUser>(
      flchUrl(`booking/teachers/${instructorId}/user`)
    )
    if (!instructor) {
      router.replace(
        {
          pathname: router.asPath.split(/[?#]/)[0],
          query: null,
        },
        undefined,
        { shallow: true }
      )
      return
    }
    let newState: BookingSelection = selectInstructor(instructor)

    const locationSlug = parseQuery(queryLocation, 'string')
    if (!locationSlug) {
      confirmState(newState)
      return
    }

    const location = (
      await axios.get<MyBLINK.BookableLocation>(
        fullFlchUrl(`booking/locations/${locationSlug}`)
      )
    ).data

    const addressId = parseQuery(queryAddressId, 'string') as string | null

    const addresses = await get<MyBLINK.BookableAddress[]>(
      flchUrl(`booking/locations/${location.id}/addresses?type=pickup`)
    )

    let address: MyBLINK.BookableAddress | null = null
    if (addressId) {
      address =
        addresses.find((address) => address.id === parseInt(addressId)) ?? null
    }
    if (!address) {
      address = addresses[0]
    }
    if (!address) {
      confirmState(newState)
      return
    }

    newState = await selectAddress(location, address, newState)

    const gearbox = parseQuery(queryGearbox, 'string') as 'manual' | 'automatic'

    if (!gearbox) {
      confirmState(newState)
      return
    }

    const locationGearboxes = await get<MyBLINK.GearboxAvailability>(
      flchUrl(`booking/locations/${location.id}/gearboxes`)
    )

    if (
      (gearbox === 'manual' && !locationGearboxes.hasManual) ||
      (gearbox === 'automatic' && !locationGearboxes.hasAutomatic)
    ) {
      confirmState(newState)
      return
    }

    newState = await selectGearbox(gearbox, newState)

    setState(newState)
    setUrlQueryLoaded(true)
  }

  const parseQuery = (
    query: string | string[] | undefined,
    expected: 'string' | 'array'
  ) => {
    if (expected === 'string') {
      return typeof query === 'string' ? query : null
    } else if (expected === 'array') {
      if (Array.isArray(query)) {
        return query
      }
      if (typeof query === 'string') {
        return [query]
      }
    }
    return null
  }

  const confirmState = (state: BookingSelection) => {
    setUrlQueryLoaded(true)
    state.config.dialogOpen = true
    setState(state)
  }

  const confirmAddress = async (
    location: MyBLINK.BookableLocation,
    address: MyBLINK.BookableAddress
  ) => {
    const updatedState = await selectAddress(location, address)
    if (updatedState.gearbox) {
      updatedState.config.dialogOpen = false
    }
    setState(updatedState)
  }

  const confirmGearbox = async (gearbox: 'manual' | 'automatic') => {
    const updatedState = await selectGearbox(gearbox)
    updatedState.config.dialogOpen = false
    setState(updatedState)
  }

  const selectAddress = async (
    location: MyBLINK.BookableLocation,
    address: MyBLINK.BookableAddress,
    localState?: BookingSelection
  ) => {
    localState = localState ?? Object.assign({}, state)

    trackEvent({
      event: 'trial_booking_location_step_done',
      params: {
        location: localState.location?.name,
      },
    })

    localState = {
      instructor: localState.instructor!,
      location,
      address,
      gearbox: null,
      mergedSlot: null,
      slot: null,
      config: {
        ...state.config,
        state: BookingState.LocationSelected,
        currentDialogStep: 'gearbox',
      },
    }

    // Automatically select gearbox if only one is available
    // const gearboxAvailability = await get<MyBLINK.GearboxAvailability>(
    //   flchUrl(`booking/locations/${location.id}/gearboxes`)
    // )
    //
    // if (gearboxAvailability.hasAutomatic && !gearboxAvailability.hasManual) {
    //   return selectGearbox('automatic', localState)
    // } else if (
    //   !gearboxAvailability.hasAutomatic &&
    //   gearboxAvailability.hasManual
    // ) {
    //   return selectGearbox('manual', localState)
    // } else if (
    //   state.gearbox === 'automatic' &&
    //   gearboxAvailability.hasAutomatic
    // ) {
    //   return selectGearbox('automatic', localState)
    // } else if (state.gearbox === 'manual' && gearboxAvailability.hasManual) {
    //   return selectGearbox('manual', localState)
    // }
    return localState
  }

  const selectGearbox = async (
    gearbox: 'manual' | 'automatic',
    localState?: BookingSelection
  ) => {
    localState = localState ?? Object.assign({}, state)

    if (localState.config.state < BookingState.LocationSelected) {
      return localState
    }

    trackEvent({
      event: 'trial_booking_transmission_type_step_done',
      params: {
        location: localState.location?.name,
      },
    })

    localState = {
      instructor: localState.instructor!,
      location: localState.location!,
      address: localState.address!,
      gearbox,
      mergedSlot: null,
      slot: null,
      config: {
        ...localState.config,
        state: BookingState.GearboxSelected,
      },
    }

    return localState
  }

  const selectInstructor = (instructor: MyBLINK.BookableUser) => {
    let localState = Object.assign({}, state)

    trackEvent({
      event: 'trial_booking_teacher_step_done',
      params: {
        location: localState.location?.name,
      },
    })

    localState = {
      instructor,
      location: null,
      address: null,
      gearbox: null,
      mergedSlot: null,
      slot: null,
      config: {
        ...localState.config,
        state: BookingState.InstructorSelected,
      },
    }

    return localState
  }

  const removeSlot = () => {
    setState((state) => {
      if (!state.mergedSlot) {
        return state
      }

      return {
        instructor: state.instructor,
        location: state.location,
        address: state.address,
        gearbox: state.gearbox,
        mergedSlot: null,
        slot: null,
        config: {
          ...state.config,
          state: BookingState.GearboxSelected,
        },
      }
    })
  }

  const confirmMergedSlot = (mergedSlot: MergedSlot) => {
    setState(selectMergedSlot(mergedSlot))
  }

  const confirmSlot = (slot: MyBLINK.BookableSlot) => {
    if (slot) {
      setState(selectSlot(slot))
    } else {
      setState((state) => {
        if (!state.mergedSlot) {
          return state
        }

        return {
          instructor: state.instructor,
          location: state.location,
          address: state.address,
          gearbox: state.gearbox,
          mergedSlot: state.mergedSlot,
          slot: null,
          config: {
            ...state.config,
            state: BookingState.MergedSlotSelected,
          },
        }
      })
    }
  }

  const selectMergedSlot = (
    mergedSlot: MergedSlot,
    localState?: BookingSelection
  ) => {
    localState = localState ?? Object.assign({}, state)

    if (localState.config.state < BookingState.InstructorSelected) {
      return localState
    }

    localState = {
      instructor: localState.instructor!,
      location: localState.location!,
      address: localState.address!,
      gearbox: localState.gearbox!,
      mergedSlot,
      slot: null,
      config: {
        ...localState.config,
        state: BookingState.MergedSlotSelected,
      },
    }

    return localState
  }

  const selectSlot = (
    slot: MyBLINK.BookableSlot,
    localState?: BookingSelection
  ) => {
    localState = localState ?? Object.assign({}, state)

    if (localState.config.state < BookingState.MergedSlotSelected) {
      return localState
    }

    trackEvent({
      event: 'trial_booking_date_step_done',
      params: {
        location: localState.location?.name,
      },
    })

    localState = {
      instructor: localState.instructor!,
      location: localState.location!,
      address: localState.address!,
      gearbox: localState.gearbox!,
      mergedSlot: localState.mergedSlot!,
      slot,
      config: {
        ...localState.config,
        state: BookingState.SlotSelected,
      },
    }

    return localState
  }

  const goToSummary = () => {
    setState((state) => {
      state = { ...state }
      state.config.state = BookingState.Summary
      return state
    })
    window.scrollTo({ top: 0 })
  }

  const goToBooking = () => {
    removeSlot()
  }

  const changeSelectedDate = (
    step: 'halfWeek' | 'week',
    direction: 'before' | 'next'
  ) => {
    let newSelectedDate = state.config.selectedDate
    const weekday =
      getDay(state.config.selectedDate) === 0
        ? 7
        : getDay(state.config.selectedDate)
    if (direction === 'before') {
      if (step === 'halfWeek') {
        newSelectedDate = sub(state.config.selectedDate, {
          days: weekday === 7 ? 4 : 3,
        })
      } else {
        newSelectedDate = sub(state.config.selectedDate, {
          weeks: 1,
        })
      }
    } else {
      if (step === 'halfWeek') {
        newSelectedDate = add(state.config.selectedDate, {
          days: weekday === 4 ? 4 : 3,
        })
      } else {
        newSelectedDate = add(state.config.selectedDate, {
          weeks: 1,
        })
      }
    }
    setState((state) => {
      state = { ...state }
      state.config.selectedDate = newSelectedDate
      return state
    })
  }

  const setSelectedDate = (date: Date) => {
    setState((state) => {
      state = { ...state }
      state.config.selectedDate = date
      return state
    })
  }

  const setStep = (step: 'halfWeek' | 'week') => {
    setState((state) => {
      state = { ...state }
      state.config.step = step
      return state
    })
  }

  const setDialogStep = (currentDialogStep: 'location' | 'gearbox') => {
    setState((state) => {
      state = { ...state }
      state.config.currentDialogStep = currentDialogStep
      return state
    })
  }

  const setDialogOpen = (open: boolean) => {
    setState((state) => {
      state = { ...state }
      state.config.dialogOpen = open
      return state
    })
  }

  return (
    <BookingContext.Provider
      value={{
        ...state,
        confirmAddress,
        confirmGearbox,
        removeSlot,
        confirmMergedSlot,
        confirmSlot,
        goToSummary,
        goToBooking,
        changeSelectedDate,
        setSelectedDate,
        setStep,
        setDialogStep,
        setDialogOpen,
      }}
    >
      {children}
    </BookingContext.Provider>
  )
}

export const useBookingContext = () => {
  const context = useContext(BookingContext)

  if (context === undefined) {
    throw new Error(
      'useBookingContext must be used within a BookingContextProvider'
    )
  }

  return context
}
